import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Select, Typography, DatePicker, Button, message } from "antd";
import moment from 'moment';
import OrdersTable from "./components/OrdersTable";
import OrderService from "../../utils/api/orders";
import UserService from "../../utils/api/users";
import LayoutPage from "../../layouts/Main";
import './components/OrdersTable.css';

const { Title } = Typography;
const { Option, OptGroup } = Select;
const { RangePicker } = DatePicker;

export default function FinishedOrdersPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
  const currentUserId = currentUser?.id;
  const currentUserCategory = currentUser?.role_data?.cat1;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [users, setUsers] = useState({ buyers: [], sellers: [] });
  const [selectedUser, setSelectedUser] = useState("all");

  const [userRole, setUserRole] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  const initialFiltersSet = useRef(false);
  const initialFetchDone = useRef(false);
  const ordersCacheRef = useRef(new Map());
  const localBaseServicesCacheRef = useRef(new Map());
  const fetchTokenRef = useRef(0);

  const getStatusName = (statusCode) => {
    const statusMap = {
      1: 'En revisión',
      2: 'En cotización',
      3: 'Pendiente de aprobación',
      4: 'En construcción',
      5: 'Inspección',
      6: 'En camino',
      7: 'Entregado',
      8: 'Rechazado',
      9: 'Cancelado',
      10: 'En corrección',
      11: 'Cancelación solicitada',
      12: 'En queja'
    };
    return statusMap[statusCode] || `Estado ${statusCode}`;
  };

  useEffect(() => {
    if (!initialFiltersSet.current) {
      const params = new URLSearchParams(location.search);
      const startDate = params.get('startDate');
      const endDate = params.get('endDate');
      const status = params.get('status');
      const serviceType = params.get('serviceType');

      let mensajesMostrados = [];
      let newDateRange = [];
      let newStatusFilter = "all";

      if (startDate && endDate) {
        const start = moment(startDate);
        const end = moment(endDate);

        if (start.isValid() && end.isValid()) {
          newDateRange = [start, end];
          mensajesMostrados.push(`Filtros de fecha: ${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`);
        }
      }

      if (status) {
        newStatusFilter = String(status);
        mensajesMostrados.push(`Estado: ${getStatusName(parseInt(status))}`);
      }

      if (serviceType) {
        console.log('Tipo de servicio:', serviceType);
      }

      setDateRange(newDateRange);
      setStatusFilter(newStatusFilter);

      if (mensajesMostrados.length > 0) {
        message.info(`Filtros aplicados: ${mensajesMostrados.join(' • ')}`);
      }

      initialFiltersSet.current = true;
    }
  }, []);

  useEffect(() => {
    const role = localStorage.getItem('role');
    setUserRole(role);
  }, []);

  useEffect(() => {
    const fetchSelectors = async () => {
      try {
        const response = await UserService.GetUserSelectors();
        if (response.data.success) {
          setUsers({
            buyers: response.data.buyers || [],
            sellers: response.data.sellers || []
          });
        }
      } catch (error) {
        console.error("Fallo al obtener los selectores:", error);
      }
    };
    if (userRole === '1') {
      fetchSelectors();
    }
  }, [userRole]);

  const fetchOrders = useCallback(async (status, dates, userSelection, search, currentPage = 1, pageSize = 15, isInitial = false) => {
    if (isInitial && initialFetchDone.current) {
      return;
    }

    const token = ++fetchTokenRef.current;
    setLoading(true);
    try {
      const startDate = dates && dates[0] ? dates[0].format('YYYY-MM-DD') : null;
      const endDate = dates && dates[1] ? dates[1].format('YYYY-MM-DD') : null;
      const statusParam = status !== "all" ? parseInt(status) : status;
      const normalizedSearch = (search || "").toLowerCase().trim();

      const normalizeId = (value) => {
        if (value === undefined || value === null) return null;
        return String(value);
      };

      const extractArray = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.data?.orders)) return payload.data.orders;
        if (Array.isArray(payload?.data?.services)) return payload.data.services;
        if (Array.isArray(payload?.orders)) return payload.orders;
        if (Array.isArray(payload?.services)) return payload.services;
        return [];
      };

      const isServiceInDateRange = (service) => {
        if (!startDate && !endDate) return true;

        const rawDate =
          service?.updated_at ||
          service?.updatedAt ||
          service?.created_at ||
          service?.createdAt;

        const m = moment(rawDate);
        if (!m.isValid()) return false;

        if (startDate && m.isBefore(moment(startDate), 'day')) return false;
        if (endDate && m.isAfter(moment(endDate), 'day')) return false;
        return true;
      };

      const serviceMatchesSearch = (service, order) => {
        if (!normalizedSearch) return true;

        const candidates = [
          service?.order_id,
          service?.orderId,
          order?.orderId,
          order?.id,
          service?.serviceId,
          service?.id,
          service?.service_id,
          service?.client_bussiness,
          order?.client_bussiness,
          order?.client_name,
          service?.title,
          order?.title,
          service?.description,
        ];

        return candidates.some((value) =>
          String(value ?? "").toLowerCase().includes(normalizedSearch)
        );
      };

      let buyerId = null;
      let sellerId = null;

      if (userRole === '3' && currentUserId) {
        buyerId = currentUserId;
      }

      if (userRole === '5' && currentUserId) {
        sellerId = currentUserId;
      }

      if (userSelection && userSelection !== 'all') {
        const [type, id] = userSelection.split('-');
        if (type === 'buyer') {
          buyerId = id;
        } else if (type === 'seller') {
          sellerId = id;
        }
      }

      const useLocalServicesMode =
        userRole === '1' || userRole === '3' || userRole === '5' || userRole === '6';

      const applyLocalFilters = (services) => {
        return services.filter((service) => {
          if (statusParam !== "all" && Number(service?.status) !== Number(statusParam)) return false;
          if (!isServiceInDateRange(service)) return false;
          if (!serviceMatchesSearch(service, { orderId: service?.orderId, title: service?.title })) return false;
          return true;
        });
      };

      const paginateLocalServices = (services) => {
        const total = services.length;
        const start = Math.max(0, (currentPage - 1) * pageSize);
        const end = start + pageSize;
        const pagedItems = services.slice(start, end);

        setOrders(pagedItems);
        setPagination(prev => ({
          ...prev,
          total,
          current: currentPage,
          pageSize,
        }));
      };

      const localCacheKey = JSON.stringify({
        role: userRole,
        buyerId: buyerId || '',
        sellerId: sellerId || '',
        currentUserId: currentUserId || '',
        category: currentUserCategory || '',
      });

      if (useLocalServicesMode && localBaseServicesCacheRef.current.has(localCacheKey)) {
        if (token === fetchTokenRef.current) {
          const baseServices = localBaseServicesCacheRef.current.get(localCacheKey) || [];
          const filteredServices = applyLocalFilters(baseServices);
          paginateLocalServices(filteredServices);
          setLoading(false);
        }

        if (isInitial) {
          initialFetchDone.current = true;
        }
        return;
      }

      const cacheKey = JSON.stringify({
        role: userRole,
        status: status || 'all',
        startDate: startDate || '',
        endDate: endDate || '',
        search: normalizedSearch,
        buyerId: buyerId || '',
        sellerId: sellerId || '',
        page: currentPage,
        pageSize,
      });
      const useCache = !useLocalServicesMode;

      if (useCache && ordersCacheRef.current.has(cacheKey)) {
        const cached = ordersCacheRef.current.get(cacheKey);
        if (token === fetchTokenRef.current) {
          setOrders(cached.items || []);
          setPagination(prev => ({
            ...prev,
            total: cached.total || 0,
            current: currentPage,
            pageSize,
          }));
          setLoading(false);
        }

        if (isInitial) {
          initialFetchDone.current = true;
        }
        return;
      }

      // Para perfiles asignados evitamos el límite del endpoint paginado
      // cargando una sola vez y paginando localmente.
      if (useLocalServicesMode) {
        const allOrdersResponse = await OrderService.All();

        let assignedClientIds = null;
        const shouldFilterByAssignedClients = userRole === '3' || userRole === '5' || buyerId || sellerId;

        if (shouldFilterByAssignedClients) {
          const assignedClientsResponse = await UserService.GetClients({
            buyerId: userRole === '3' ? currentUserId : buyerId,
            sellerId: userRole === '5' ? currentUserId : sellerId,
          });

          const assignedClients = extractArray(assignedClientsResponse);
          assignedClientIds = new Set(
            assignedClients
              .map((client) => normalizeId(client?.id || client?.client_id || client?.clientId))
              .filter(Boolean)
          );
        }

        if (token !== fetchTokenRef.current) {
          return;
        }

        const allOrders = extractArray(allOrdersResponse?.data || allOrdersResponse);

        const allAssignedServices = [];
        const supplierCategoryId = parseInt(currentUserCategory, 10);
        const normalizedCurrentUserId = normalizeId(currentUserId);

        allOrders.forEach((order) => {
          const shouldFilterByAssigned = assignedClientIds !== null;

          const orderClientId = normalizeId(
            order?.client_id ||
            order?.clientId ||
            order?.buyer_id ||
            order?.buyerId ||
            order?.client?.id
          );
          const orderBelongsToAssignedClient =
            shouldFilterByAssigned && orderClientId && assignedClientIds.has(orderClientId);
          const services = Array.isArray(order?.services) ? order.services : [];

          if (shouldFilterByAssigned && assignedClientIds.size === 0) {
            return;
          }

          services.forEach((service) => {
            if (userRole === '6') {
              const statusNumber = Number(service?.status);
              const serviceCategory = parseInt(service?.cat ?? service?.category_id ?? service?.categoryId, 10);
              const serviceSupplierId = normalizeId(service?.supplier_id || service?.supplierId);

              if (Number.isFinite(supplierCategoryId) && serviceCategory !== supplierCategoryId) return;
              if (statusNumber === 1) return;

              const isRelevantForSupplier =
                statusNumber === 2 ||
                (statusNumber >= 3 && serviceSupplierId === normalizedCurrentUserId);

              if (!isRelevantForSupplier) return;
            }

            const serviceClientId = normalizeId(
              service?.client_id ||
              service?.clientId ||
              service?.buyer_id ||
              service?.buyerId ||
              orderClientId
            );

            const serviceBelongsToAssignedClient =
              orderBelongsToAssignedClient ||
              (shouldFilterByAssigned && serviceClientId && assignedClientIds.has(serviceClientId));

            if (shouldFilterByAssigned && !serviceBelongsToAssignedClient) return;

            allAssignedServices.push({
              ...service,
              orderId: service?.order_id || service?.orderId || order?.orderId || order?.id,
              serviceId: service?.serviceId || service?.id || service?.service_id,
              client_bussiness: service?.client_bussiness || order?.client_bussiness || order?.client_name || 'N/A',
              title: service?.title || order?.title || 'N/A',
              description: service?.description || 'N/A',
            });
          });
        });

        const seen = new Set();
        const deduped = allAssignedServices.filter((service) => {
          const key = `${service?.serviceId || service?.id || service?.service_id || 'x'}-${service?.orderId || service?.order_id || 'y'}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        const sorted = deduped.sort((a, b) => {
          const aTime = moment(a?.updated_at || a?.updatedAt || a?.created_at || a?.createdAt).valueOf();
          const bTime = moment(b?.updated_at || b?.updatedAt || b?.created_at || b?.createdAt).valueOf();
          return bTime - aTime;
        });

        localBaseServicesCacheRef.current.set(localCacheKey, sorted);
        const filteredServices = applyLocalFilters(sorted);
        paginateLocalServices(filteredServices);

        if (isInitial) {
          initialFetchDone.current = true;
        }

        return;
      }

      const requestPage = async (page) => {
        if (statusParam && statusParam !== "all") {
          return OrderService.FilterServices(statusParam, page, startDate, endDate, normalizedSearch || undefined, buyerId, sellerId);
        }
        return OrderService.ViewServices(page, startDate, endDate, normalizedSearch || undefined, buyerId, sellerId);
      };

      const pageResponse = await requestPage(currentPage);
      if (token !== fetchTokenRef.current) {
        return;
      }

      const pageServices = pageResponse?.data?.services || pageResponse?.data?.orders || [];

      const mapService = (service) => ({ ...service, serviceId: service.id, orderId: service.order_id });
      const dedupe = (services) => {
        const seen = new Set();
        const deduped = [];
        services.forEach((service) => {
          const key = `${service?.id || service?.serviceId || 'x'}-${service?.order_id || service?.orderId || 'y'}`;
          if (!seen.has(key)) {
            seen.add(key);
            deduped.push(service);
          }
        });
        return deduped;
      };

      const mappedPage = dedupe(Array.isArray(pageServices) ? pageServices : []).map(mapService);
      const remoteTotal =
        pageResponse?.data?.pagination?.total_services ||
        pageResponse?.data?.pagination?.total ||
        mappedPage.length;

      setOrders(mappedPage);
      setPagination(prev => ({
        ...prev,
        total: remoteTotal,
        current: currentPage,
        pageSize,
      }));
      if (useCache) {
        ordersCacheRef.current.set(cacheKey, {
          items: mappedPage,
          total: remoteTotal,
        });
      }

      if (isInitial) {
        initialFetchDone.current = true;
      }

    } catch (error) {
      console.error("Fallo al obtener las órdenes:", error);
      if (token === fetchTokenRef.current) {
        setOrders([]);
        setPagination(prev => ({ ...prev, total: 0, current: 1 }));
      }
    } finally {
      if (token === fetchTokenRef.current) {
        setLoading(false);
      }
    }
  }, [userRole, currentUserId, currentUserCategory]);

  useEffect(() => {
    if (initialFiltersSet.current && !initialFetchDone.current && userRole !== null) {
      fetchOrders(
        statusFilter,
        dateRange,
        selectedUser,
        searchText,
        pagination.current,
        pagination.pageSize,
        true
      );
    }
  }, [userRole, statusFilter, dateRange, selectedUser, searchText, pagination.current, pagination.pageSize, fetchOrders]);

  useEffect(() => {
    if (initialFetchDone.current) {
      fetchOrders(
        statusFilter,
        dateRange,
        selectedUser,
        searchText,
        pagination.current,
        pagination.pageSize,
        false
      );
    }
  }, [statusFilter, dateRange, selectedUser, searchText, pagination.current, pagination.pageSize, fetchOrders]);

  const handleFilterChange = (value) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setStatusFilter(value);

    const params = new URLSearchParams(location.search);
    if (value && value !== "all") {
      params.set('status', value);
    } else {
      params.delete('status');
    }
    navigate({ search: params.toString() }, { replace: true });

    if (value !== "all") {
      message.info(`Filtro cambiado a: ${getStatusName(parseInt(value))}`);
    }
  };

  const handleDateChange = (dates) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setDateRange(dates || []);

    const params = new URLSearchParams(location.search);
    if (dates && dates.length === 2) {
      params.set('startDate', dates[0].format('YYYY-MM-DD'));
      params.set('endDate', dates[1].format('YYYY-MM-DD'));
    } else {
      params.delete('startDate');
      params.delete('endDate');
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleUserChange = (value) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setSelectedUser(value);
  };

  const handleSearchChange = (value) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setSearchText(value);
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize || prev.pageSize,
    }));
  };

  const handleResetFilters = () => {
    setStatusFilter("all");
    setDateRange([]);
    setSelectedUser("all");
    setSearchText("");
    setPagination(prev => ({ ...prev, current: 1 }));

    navigate({ search: '' }, { replace: true });
    message.info('Todos los filtros han sido restablecidos');
  };

  return (
    <LayoutPage>
      <div className="orders-card">
        <Row gutter={[24, 16]}>
          <Col xs={24}>
            <Title level={3}>Servicios generales</Title>
          </Col>
          <Col xs={24} md={8} lg={6}>
            <Select
              value={statusFilter}
              style={{ width: '100%' }}
              onChange={handleFilterChange}
            >
              <Option value="all">Todos los Estados</Option>
              <Option value="1">En revisión</Option>
              <Option value="2">En cotización</Option>
              <Option value="3">Pendiente de aprobación</Option>
              <Option value="4">En construcción</Option>
              <Option value="5">Inspección</Option>
              <Option value="6">En camino</Option>
              <Option value="7">Entregado</Option>
              <Option value="8">Rechazado</Option>
              <Option value="9">Cancelado</Option>
              <Option value="10">En corrección</Option>
              <Option value="11">Cancelacion solicitada</Option>
              <Option value="12">En queja</Option>
            </Select>
          </Col>
          {userRole === '1' && (
            <Col xs={24} md={8} lg={6}>
              <Select
                value={selectedUser}
                style={{ width: '100%' }}
                onChange={handleUserChange}
              >
                <Option value="all">Vendedores y Compradores</Option>
                <OptGroup label="Compradores">
                  {users.buyers.map(buyer => (
                    <Option key={`buyer-${buyer.id}`} value={`buyer-${buyer.id}`}>{buyer.name}</Option>
                  ))}
                </OptGroup>
                <OptGroup label="Vendedores">
                  {users.sellers.map(seller => (
                    <Option key={`seller-${seller.id}`} value={`seller-${seller.id}`}>{seller.name}</Option>
                  ))}
                </OptGroup>
              </Select>
            </Col>
          )}
          <Col xs={24} md={8} lg={6}>
            <RangePicker
              value={dateRange}
              style={{ width: '100%' }}
              onChange={handleDateChange}
            />
          </Col>
          <Col xs={24} md={8} lg={6}>
            <Button onClick={handleResetFilters}>Restablecer Filtros</Button>
          </Col>
          <Col xs={24}>
            <OrdersTable
              openOrders={orders}
              loading={loading}
              pagination={pagination}
              onTableChange={handleTableChange}
              searchText={searchText}
              onSearchChange={handleSearchChange}
            />
          </Col>
        </Row>
      </div>
    </LayoutPage>
  );
}